import { IconButton, SvgClose, Text } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { startVideoSettingsFlow } from 'actions/editor';
import { MOBILE_MAX_WIDTH } from 'constants/breakpoints';
import { STORE_FILLER_WORD_RANGES } from 'constants/featureFlag';
import { handleToggleTranscriptCreator } from 'creators/shareVideo';
import { TRANSCRIPT_PANEL_MODE_VIEW } from 'reducers/shareVideo';
import { selectUserIsOwner } from 'selectors/shareVideo';
import {
  selectIsTranscriptSearchOpen,
  selectShowDefaultTranscriptHeader,
  selectShowTranscriptCorrectionHeader,
  selectShowTranscriptToViewer,
  selectTranscriptPanelMode,
} from 'selectors/transcripts';
import { isMobile } from 'utilities/device';

import useMedia from 'hooks/useMedia';

import FillerWordRemoval from './fillerWordRemoval';
import TranscriptHeaderIcons from '../transcript-header-icons';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import TranscriptHeaderCorrections from 'components/share-video/transcript/transcript-header-corrections';
import TranscriptHeaderSearch from 'components/share-video/transcript/transcript-header-search';

import styles from './styles.module.less';

const TranscriptHeader = ({
  onExit,
  showFullHeader,
}: {
  onExit: () => void;
  showFullHeader: boolean;
}): JSX.Element => {
  const dispatch = useDispatch();
  const isSearchOpen = useSelector(selectIsTranscriptSearchOpen);
  const panelMode = useSelector(selectTranscriptPanelMode);
  const showCorrectionsHeader = useSelector<unknown, boolean>(
    selectShowTranscriptCorrectionHeader
  );
  const showDefaultHeader = useSelector<unknown, boolean>(
    selectShowDefaultTranscriptHeader
  );
  const currentUserIsOwner = useSelector<unknown, boolean>(selectUserIsOwner);
  const showTranscriptToViewer = useSelector(selectShowTranscriptToViewer);
  const [fillerRemovalDropdownOpen, setFillerRemovalDropdownOpen] =
    useState(false);
  const iconSize = useMedia(
    [`(max-width: ${MOBILE_MAX_WIDTH}px)`],
    ['medium'],
    'small'
  );

  const showFillerWordRemoval =
    !isMobile &&
    !isSearchOpen &&
    currentUserIsOwner &&
    panelMode === TRANSCRIPT_PANEL_MODE_VIEW;

  return (
    <>
      {showDefaultHeader && (
        <div className={classNames(styles.header, styles.headerDefault)}>
          <div className={styles.headerFirstLine}>
            <Text fontWeight="bold">Transcript</Text>
            <div className="flex items:center">
              <TranscriptHeaderIcons />
              <IconButton
                altText="Close"
                size={iconSize}
                icon={<SvgClose />}
                onClick={onExit}
              />
            </div>
          </div>
          {currentUserIsOwner && showTranscriptToViewer === false && (
            <div
              className={classNames([
                styles.visibilityNotice,
                {
                  [styles.scrolled]:
                    !showFullHeader && !fillerRemovalDropdownOpen,
                },
              ])}
            >
              Visible only to you.{' '}
              <button
                className={styles.linkButton}
                onClick={e => {
                  e.preventDefault();
                  dispatch(handleToggleTranscriptCreator());
                  dispatch(startVideoSettingsFlow());
                }}
              >
                Change
              </button>
            </div>
          )}
        </div>
      )}

      {showCorrectionsHeader && (
        <TranscriptHeaderCorrections iconSize={iconSize} />
      )}

      {showFillerWordRemoval && (
        <FeatureFlaggedComponent
          flag={STORE_FILLER_WORD_RANGES}
          activationValues={[true]}
        >
          <FillerWordRemoval
            showFullHeader={showFullHeader}
            iconSize={iconSize}
            dropdownOpen={fillerRemovalDropdownOpen}
            setDropdownOpen={setFillerRemovalDropdownOpen}
          />
        </FeatureFlaggedComponent>
      )}

      {isSearchOpen && (
        <TranscriptHeaderSearch
          iconSize={iconSize}
          currentUserIsOwner={currentUserIsOwner}
        />
      )}
    </>
  );
};

export default TranscriptHeader;
